import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiAlertTriangle,
  FiActivity,
  FiUserCheck,
  FiCpu,
  FiPieChart,
} from "react-icons/fi";
import axios from "axios";
import { getApiUrl } from "../utils/api";
import "../styles/teacherDashboard.css";
import { UserContext } from "../Context";

import LineChart from "../components/dashboard/visuals/LineChart";
import DonutChart from "../components/dashboard/visuals/DonutChart";
import StackedBarChart from "../components/dashboard/visuals/StackedBarChart";
import AlertCard from "../components/dashboard/visuals/AlertCard";
import QualityMetrics from "../components/dashboard/visuals/QualityMetrics";
import QueryOverviewCard from "../components/dashboard/visuals/QueryOverviewCard";
import StudentProfiles from "../components/dashboard/visuals/StudentProfiles";
import EngagementDistributionCard from "../components/dashboard/visuals/EngagementDistributionCard";
import RecentQuestionsCard from "../components/dashboard/visuals/RecentQuestionsCard";
import TopStudentsCard from "../components/dashboard/visuals/TopStudentsCard";
import MarkdownRenderer from "../components/MarkdownRenderer";
import {
  formatSolutionLevelLabel,
  formatSolutionTypeLabel,
} from "../utils/solutionLabels";

const INITIAL_STATE = {
  alerts: [],
  queryVolumeData: [],
  engagementDistribution: [],
  activeStudents: { current: 0, today: 0, highEngagement: 0 },
  recentActivity: [],
  timeHeatmap: [],
  conceptDifficulty: [],
  commonErrors: [],
  topicPerformance: [],
  hintUsage: [],
  followUpPatterns: [],
  aiResponseQuality: {
    responseAccuracy: 0,
    studentSatisfaction: 0,
    citationUsage: 0,
    helpfulResponses: 0,
    totalResponses: 0,
  },
  queryOverview: {
    totalQueries: 0,
    avgPerStudent: 0,
    peakHour: "N/A",
    activeToday: 0,
  },
  topActiveStudents: [],
  studentProgression: [],
  studentComparison: [],
  studentProfiles: [],
  progressTimeline: [],
  feedbackSummary: {
    totalFeedback: 0,
    upvotes: 0,
    downvotes: 0,
    flaggedStudents: 0,
  },
  alertSettings: {},
  exportOptions: [],
};

const InsightSection = ({ id, title, eyebrow, summary, children }) => (
  <section className="dashboard-section" aria-labelledby={id}>
    <div className="section-intro">
      <div>
        {eyebrow && <p className="section-eyebrow">{eyebrow}</p>}
        <h2 id={id} className="section-title">
          {title}
        </h2>
        {summary && <p className="section-summary">{summary}</p>}
      </div>
    </div>
    {children}
  </section>
);

const TeachersDashboard = () => {
  const { courseNumber, courseName } = useParams();
  const navigate = useNavigate();

  const { user } = React.useContext(UserContext);

  const [dashboardData, setDashboardData] = useState(INITIAL_STATE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeInsight, setActiveInsight] = useState(null);
  const [selectedConceptDetail, setSelectedConceptDetail] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [activeConversationMeta, setActiveConversationMeta] = useState(null);
  const [genaiEnabled, setGenaiEnabled] = useState(false);
  const [genaiAvailable, setGenaiAvailable] = useState(false);
  const [genaiLoading, setGenaiLoading] = useState(false);
  const [genaiError, setGenaiError] = useState("");
  const [conversationSummary, setConversationSummary] = useState({
    loading: false,
    text: "",
    error: "",
  });
  const [conversationChallenges, setConversationChallenges] = useState({
    loading: false,
    text: "",
    error: "",
  });
  const [selectedKeywordFilter, setSelectedKeywordFilter] = useState("");
  const [copyNotice, setCopyNotice] = useState("");

  const [studyFeaturesEnabled, setStudyFeaturesEnabled] = useState(true);
  const [studyFeaturesLoading, setStudyFeaturesLoading] = useState(false);
  const [studyFeaturesError, setStudyFeaturesError] = useState("");

  const canManageStudyFeatures = user?.role === "teacher";

  const fetchCourseFeatureFlags = useCallback(async () => {
    if (!courseName || !courseNumber) return;
    try {
      setStudyFeaturesLoading(true);
      setStudyFeaturesError("");
      const response = await axios.post(
        getApiUrl("/api/course/feature-flags/get"),
        {
          course_name: courseName,
          course_number: courseNumber,
        },
        { withCredentials: true }
      );
      const flags = response?.data?.feature_flags;
      if (flags && typeof flags.study_features_enabled === "boolean") {
        setStudyFeaturesEnabled(flags.study_features_enabled);
      } else {
        setStudyFeaturesEnabled(true);
      }
    } catch (err) {
      console.error("Failed to load course feature flags:", err);
      setStudyFeaturesError(err?.message || "Failed to load feature flags");
    } finally {
      setStudyFeaturesLoading(false);
    }
  }, [courseName, courseNumber]);

  const toggleStudyFeatures = useCallback(async () => {
    if (!canManageStudyFeatures || !courseName || !courseNumber) return;
    const nextValue = !studyFeaturesEnabled;
    try {
      setStudyFeaturesLoading(true);
      setStudyFeaturesError("");
      setStudyFeaturesEnabled(nextValue);
      const response = await axios.post(
        getApiUrl("/api/course/feature-flags/set"),
        {
          course_name: courseName,
          course_number: courseNumber,
          study_features_enabled: nextValue,
        },
        { withCredentials: true }
      );
      const flags = response?.data?.feature_flags;
      if (flags && typeof flags.study_features_enabled === "boolean") {
        setStudyFeaturesEnabled(flags.study_features_enabled);
      }
    } catch (err) {
      console.error("Failed to update course feature flags:", err);
      setStudyFeaturesError(err?.message || "Failed to update feature flags");
      setStudyFeaturesEnabled((prev) => !prev);
    } finally {
      setStudyFeaturesLoading(false);
    }
  }, [canManageStudyFeatures, courseName, courseNumber, studyFeaturesEnabled]);

  const resetConversationInsights = useCallback(() => {
    setConversationSummary({ loading: false, text: "", error: "" });
    setConversationChallenges({ loading: false, text: "", error: "" });
  }, []);

  const fetchDashboardData = useCallback(async () => {
    if (!courseNumber || !courseName) {
      setError("Missing course information");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.post(
        getApiUrl("/api/teacher/dashboard"),
        {
          course_number: courseNumber,
          course_name: courseName,
          include_genai: false,
        },
        { withCredentials: true }
      );

      const payload = response?.data?.data;
      if (!payload) {
        throw new Error("No dashboard data returned");
      }

      const genaiMeta = payload.genai || {};
      setGenaiEnabled(Boolean(genaiMeta.enabled));
      setGenaiAvailable(
        typeof genaiMeta.available === "boolean" ? genaiMeta.available : true
      );
      setGenaiError("");

      setDashboardData({
        ...INITIAL_STATE,
        ...payload,
      });
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError(err.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [courseNumber, courseName]);

  const fetchGenaiInsights = useCallback(async () => {
    if (!courseNumber || !courseName) {
      return;
    }
    setGenaiLoading(true);
    setGenaiError("");
    try {
      const response = await axios.post(
        getApiUrl("/api/teacher/dashboard"),
        {
          course_number: courseNumber,
          course_name: courseName,
          include_genai: true,
        },
        { withCredentials: true }
      );
      const payload = response?.data?.data;
      if (!payload) {
        throw new Error("No GenAI insights returned");
      }
      const genaiMeta = payload.genai || {};
      setGenaiEnabled(Boolean(genaiMeta.enabled));
      setGenaiAvailable(
        typeof genaiMeta.available === "boolean" ? genaiMeta.available : true
      );
      setDashboardData((prev) => ({
        ...prev,
        conceptDifficulty: payload.conceptDifficulty || [],
        topicPerformance: payload.topicPerformance || [],
      }));
    } catch (err) {
      console.error("GenAI insights error:", err);
      setGenaiError(err?.message || "Unable to load GenAI insights");
    } finally {
      setGenaiLoading(false);
    }
  }, [courseNumber, courseName]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    fetchCourseFeatureFlags();
  }, [fetchCourseFeatureFlags]);

  useEffect(() => {
    if (!selectedConceptDetail) return;
    const updated = (dashboardData.conceptDifficulty || []).find(
      (concept) => concept.concept === selectedConceptDetail.concept
    );
    if (!updated || !(updated.details || []).length) {
      setSelectedConceptDetail(null);
    } else if (updated !== selectedConceptDetail) {
      setSelectedConceptDetail(updated);
    }
  }, [dashboardData.conceptDifficulty, selectedConceptDetail]);

  useEffect(() => {
    if (!selectedConceptDetail) {
      setSelectedKeywordFilter("");
    }
  }, [selectedConceptDetail]);

  const chartPalettes = useMemo(
    () => ({
      lines: {
        queries: "#0ea5e9",
        timeline: "#f97316",
        followups: "#a855f7",
      },
      bars: {
        engagement: ["#38bdf8", "#818cf8", "#f472b6", "#fb923c"],
        concept: [
          "#34d399",
          "#60a5fa",
          "#facc15",
          "#fb7185",
          "#a78bfa",
          "#f97316",
        ],
      },
      donut: ["#22c55e", "#f97316", "#a855f7", "#6366f1", "#f43f5e"],
      stacked: {
        hintUsage: ["#c084fc", "#34d399"],
      },
    }),
    []
  );

  const getHeatmapColor = useMemo(
    () =>
      (value, maxValue = 0) => {
        const safeMax = Math.max(maxValue, 1);
        const intensity = Math.min(value / safeMax, 1);
        if (intensity >= 0.75) return "#4c1d95";
        if (intensity >= 0.5) return "#6d28d9";
        if (intensity >= 0.25) return "#8b5cf6";
        return "#c4b5fd";
      },
    []
  );

  const handleBack = useCallback(() => {
    navigate("/courses");
  }, [navigate]);

  const openConceptDetail = useCallback((concept) => {
    if (!concept || !(concept.details || []).length) {
      return;
    }
    setSelectedConceptDetail(concept);
  }, []);

  const closeConceptDetail = useCallback(() => {
    setSelectedConceptDetail(null);
  }, []);
  const openConversationDetail = useCallback(
    (detail, metaOverride = null) => {
      if (!detail || !(detail.conversation || []).length) {
        return;
      }
      setSelectedConversation(detail);
      const sessionOwner = metaOverride?.sessionOwner || detail.username;
      const sessionId = metaOverride?.sessionId || detail.sessionId;
      const studentName =
        metaOverride?.studentName || detail.student || detail.username;
      if (sessionOwner && sessionId) {
        setActiveConversationMeta({
          sessionOwner,
          sessionId,
          studentName,
        });
      } else {
        setActiveConversationMeta(null);
      }
      resetConversationInsights();
    },
    [resetConversationInsights]
  );

  const closeConversationDetail = useCallback(() => {
    setSelectedConversation(null);
    setActiveConversationMeta(null);
    resetConversationInsights();
  }, [resetConversationInsights]);

  const renderHighlightedText = useCallback((text = "", snippet = "") => {
    if (!text || !snippet) {
      return text;
    }
    const lowerText = text.toLowerCase();
    const lowerSnippet = snippet.toLowerCase();
    const index = lowerText.indexOf(lowerSnippet);
    if (index === -1) {
      return text;
    }
    const before = text.slice(0, index);
    const match = text.slice(index, index + snippet.length);
    const after = text.slice(index + snippet.length);
    return (
      <>
        {before}
        <mark className="conversation-highlight">{match}</mark>
        {after}
      </>
    );
  }, []);

  const inspectStudentSession = useCallback(
    async ({ sessionId, sessionOwner, studentName, snippet }) => {
      if (!sessionId || !sessionOwner) {
        throw new Error("Missing session reference.");
      }
      if (!courseName || !courseNumber) {
        throw new Error("Missing course context.");
      }
      const limit = 200;
      const combinedHistory = [];
      let total = Number.POSITIVE_INFINITY;
      let page = 1;
      try {
        while (combinedHistory.length < total) {
          const response = await axios.post(
            getApiUrl("/api/session/chat/paginated"),
            {
              session_owner: sessionOwner,
              session_id: sessionId,
              course_name: courseName,
              course_number: courseNumber,
              page,
              limit,
            },
            { withCredentials: true }
          );
          const payload = response?.data;
          if (!payload?.data) {
            throw new Error("Chat history unavailable.");
          }
          combinedHistory.push(...payload.data);
          total =
            typeof payload.total === "number"
              ? payload.total
              : combinedHistory.length;
          if (!payload.total || combinedHistory.length >= payload.total) {
            break;
          }
          page += 1;
        }
        const conversation = combinedHistory
          .map((entry) => {
            const text = (entry?.text || "").trim();
            if (!text) {
              return null;
            }
            const role =
              (entry?.type || "").toLowerCase() === "query"
                ? "Student"
                : "Tutor";
            return { role, text };
          })
          .filter(Boolean);
        if (!conversation.length) {
          throw new Error("No chat messages were captured for this session.");
        }
        openConversationDetail(
          {
            student: studentName || sessionOwner || "Student",
            username: sessionOwner,
            sessionId,
            snippet,
            conversation,
          },
          { sessionOwner, sessionId, studentName }
        );
      } catch (error) {
        console.error("Failed to load session conversation:", error);
        throw new Error(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load this session."
        );
      }
    },
    [courseName, courseNumber, openConversationDetail]
  );

  const handleDownloadConversation = useCallback(
    async ({ sessionId, sessionOwner, studentName }) => {
      if (!sessionId || !sessionOwner || !courseName || !courseNumber) {
        throw new Error("Missing session details for download.");
      }
      try {
        const response = await axios.post(
          getApiUrl("/api/session/chat/export"),
          {
            session_owner: sessionOwner,
            session_id: sessionId,
            course_name: courseName,
            course_number: courseNumber,
            student_name: studentName,
          },
          {
            withCredentials: true,
            responseType: "blob",
          }
        );
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        const safeName = (studentName || sessionOwner || "student")
          .replace(/\s+/g, "_")
          .toLowerCase();
        anchor.href = url;
        anchor.download = `${safeName}_${sessionId}_transcript.pdf`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Conversation download failed:", error);
        throw new Error(
          error?.response?.data?.message ||
            "Unable to download this transcript right now."
        );
      }
    },
    [courseName, courseNumber]
  );

  const handleGenerateConversationSummary = useCallback(async () => {
    if (
      !activeConversationMeta?.sessionOwner ||
      !courseName ||
      !courseNumber
    ) {
      return;
    }
    setConversationSummary({ loading: true, text: "", error: "" });
    try {
      const response = await axios.post(
        getApiUrl("/api/session/summary"),
        {
          session_owner: activeConversationMeta.sessionOwner,
          session_id: activeConversationMeta.sessionId,
          course_name: courseName,
          course_number: courseNumber,
        },
        { withCredentials: true }
      );
      const summaryText = response?.data?.summary;
      setConversationSummary({
        loading: false,
        text: summaryText || "Summary not available for this session.",
        error: "",
      });
    } catch (error) {
      console.error("Summary error:", error);
      setConversationSummary({
        loading: false,
        text: "",
        error:
          error?.response?.data?.message ||
          "Unable to generate summary right now.",
      });
    }
  }, [activeConversationMeta, courseName, courseNumber]);

  const handleAnalyzeConversationChallenges = useCallback(async () => {
    if (
      !activeConversationMeta?.sessionOwner ||
      !courseName ||
      !courseNumber
    ) {
      return;
    }
    setConversationChallenges({ loading: true, text: "", error: "" });
    try {
      const response = await axios.post(
        getApiUrl("/api/session/challenges"),
        {
          session_owner: activeConversationMeta.sessionOwner,
          session_id: activeConversationMeta.sessionId,
          course_name: courseName,
          course_number: courseNumber,
        },
        { withCredentials: true }
      );
      const challengesText = response?.data?.challenges;
      setConversationChallenges({
        loading: false,
        text:
          challengesText || "No challenges were identified for this session.",
        error: "",
      });
    } catch (error) {
      console.error("Challenge analysis error:", error);
      setConversationChallenges({
        loading: false,
        text: "",
        error:
          error?.response?.data?.message ||
          "Unable to analyze challenges right now.",
      });
    }
  }, [activeConversationMeta, courseName, courseNumber]);

  const getRecencyBadge = useCallback((concept) => {
    const iso = concept?.lastSessionAt;
    if (!iso) return null;
    const diffMs = Date.now() - Date.parse(iso);
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays <= 3) return { label: "New", tone: "fresh" };
    if (diffDays >= 14) return { label: "Dormant", tone: "stale" };
    return null;
  }, []);

  const renderSparkline = useCallback((trend = []) => {
    if (!trend.length) return null;
    const counts = trend.map((point) => point.count || 0);
    const max = Math.max(...counts, 1);
    const points = counts.map((count, index) => {
      const x = (index / (counts.length - 1 || 1)) * 100;
      const y = 30 - (count / max) * 30;
      return `${x},${y}`;
    });
    return (
      <svg viewBox="0 0 100 30" className="concept-sparkline">
        <polyline points={points.join(" ")} />
      </svg>
    );
  }, []);

  const handleCopyConceptSessions = useCallback(() => {
    if (!selectedConceptDetail) return;
    const lines = (selectedConceptDetail.details || []).map(
      (detail) => `${detail.student || "Student"} - ${detail.sessionId}`
    );
    if (!lines.length) return;
    const payload = lines.join("\n");
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(payload).then(
        () => {
          setCopyNotice("Copied student list");
          setTimeout(() => setCopyNotice(""), 2000);
        },
        () => {
          setCopyNotice("Unable to copy");
          setTimeout(() => setCopyNotice(""), 2000);
        }
      );
    } else {
      setCopyNotice("Clipboard unavailable");
      setTimeout(() => setCopyNotice(""), 2000);
    }
  }, [selectedConceptDetail]);

  const overview = dashboardData.queryOverview || INITIAL_STATE.queryOverview;
  const studentProfilesCount = dashboardData.studentProfiles?.length || 0;
  const conceptSessionTotal = useMemo(() => {
    return (dashboardData.conceptDifficulty || []).reduce(
      (sum, concept) => sum + (concept?.sessionCount ?? 0),
      0
    );
  }, [dashboardData.conceptDifficulty]);
  const conceptStudentCount = useMemo(() => {
    const unique = new Set();
    (dashboardData.conceptDifficulty || []).forEach((concept) => {
      (concept?.students || []).forEach((student) => {
        if (student) {
          unique.add(student);
        }
      });
    });
    return unique.size;
  }, [dashboardData.conceptDifficulty]);
  const studentFrequency = useMemo(() => {
    const map = {};
    (dashboardData.conceptDifficulty || []).forEach((concept) => {
      (concept?.students || []).forEach((student) => {
        if (!student) return;
        map[student] = (map[student] || 0) + 1;
      });
    });
    return map;
  }, [dashboardData.conceptDifficulty]);
  const recurringStudents = useMemo(() => {
    return Object.entries(studentFrequency)
      .sort((a, b) => b[1] - a[1])
      .map(([student, count]) => ({ student, count }));
  }, [studentFrequency]);
  const conceptModeSnapshot = useMemo(() => {
    const levelMap = {};
    const typeMap = {};
    const increment = (bucket, label) => {
      if (!label || label === "Not specified") return;
      bucket[label] = (bucket[label] || 0) + 1;
    };
    (dashboardData.conceptDifficulty || []).forEach((concept) => {
      (concept?.details || []).forEach((detail) => {
        if (detail?.solutionLevel) {
          increment(
            levelMap,
            formatSolutionLevelLabel(detail.solutionLevel)
          );
        }
        if (detail?.solutionType) {
          increment(
            typeMap,
            formatSolutionTypeLabel(detail.solutionType)
          );
        }
      });
    });
    const toList = (bucket) =>
      Object.entries(bucket)
        .sort((a, b) => b[1] - a[1])
        .map(([label, count]) => ({ label, count }));
    return {
      levels: toList(levelMap),
      types: toList(typeMap),
    };
  }, [dashboardData.conceptDifficulty]);
  const topConceptKeywords = useMemo(() => {
    const counter = {};
    (dashboardData.conceptDifficulty || []).forEach((concept) => {
      (concept?.keywords || []).forEach((keyword) => {
        const normalized = (keyword || "").trim();
        if (!normalized) return;
        counter[normalized] = (counter[normalized] || 0) + 1;
      });
    });
    return Object.entries(counter)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([keyword, count]) => ({ keyword, count }));
  }, [dashboardData.conceptDifficulty]);
  const liveAlerts = dashboardData.alerts?.length || 0;

  const hintsThisWeek = useMemo(() => {
    const series = dashboardData.hintUsage || [];
    if (!series.length) return 0;
    const latest = series[series.length - 1] || {};
    return latest.hints ?? 0;
  }, [dashboardData.hintUsage]);

  const alertTypeSummary = useMemo(() => {
    const typeLabels = {
      inactive: "Inactivity",
      struggle: "Repeated struggles",
      feedback: "Negative feedback",
    };
    const counts = {};
    (dashboardData.alerts || []).forEach((alert) => {
      if (!typeLabels[alert.type]) return;
      counts[alert.type] = (counts[alert.type] || 0) + 1;
    });
    return Object.keys(typeLabels)
      .map((type) => ({
        label: typeLabels[type],
        count: counts[type] || 0,
      }))
      .filter((item) => item.count > 0);
  }, [dashboardData.alerts]);
  const studentProfileList = useMemo(
    () => (dashboardData.studentProfiles || []).slice(),
    [dashboardData.studentProfiles]
  );
  const feedbackAlerts = useMemo(() => {
    return studentProfileList
      .filter((profile) =>
        (profile.issues || []).includes("Received downvotes")
      )
      .map((profile) => ({
        name: profile.name || profile.username || "Student",
        lastActive: profile.lastActive || "No activity logged",
        totalQueries: profile.totalQueries ?? 0,
      }))
      .slice(0, 4);
  }, [studentProfileList]);
  const followUpHotspots = useMemo(() => {
    return studentProfileList
      .filter((profile) => (profile.followUps ?? 0) > 0)
      .sort((a, b) => (b.followUps ?? 0) - (a.followUps ?? 0))
      .slice(0, 4)
      .map((profile) => ({
        name: profile.name || profile.username || "Student",
        followUps: profile.followUps ?? 0,
        multiTurnSessions: profile.multiTurnSessions ?? 0,
      }));
  }, [studentProfileList]);
  const coolingOffList = useMemo(() => {
    return studentProfileList
      .filter(
        (profile) =>
          (profile.todayQueries ?? 0) === 0 && (profile.weeklyQueries ?? 0) >= 5
      )
      .sort((a, b) => (b.weeklyQueries ?? 0) - (a.weeklyQueries ?? 0))
      .slice(0, 4)
      .map((profile) => ({
        name: profile.name || profile.username || "Student",
        lastActive: profile.lastActive || "No activity logged",
        weeklyQueries: profile.weeklyQueries ?? 0,
      }));
  }, [studentProfileList]);
  const conceptStackList = useMemo(() => {
    return studentProfileList
      .map((profile) => ({
        name: profile.name || profile.username || "Student",
        username: profile.username,
        concepts: profile.concepts || [],
      }))
      .filter((profile) => profile.concepts.length >= 2)
      .slice(0, 4);
  }, [studentProfileList]);

  const renderEngagementPulseSection = () => (
    <InsightSection
      id="engagement-pulse"
      eyebrow="Story 1 · Student Engagement"
      title="Engagement Pulse"
      summary="Usage snapshot plus live roster momentum keeps every engagement conversation grounded in the latest context."
    >
      <div className="engagement-pulse-grid">
        <div className="engagement-pulse-column">
          <QueryOverviewCard overview={dashboardData.queryOverview} />
          <LineChart
            title="Weekly Query Volume"
            data={dashboardData.queryVolumeData}
            xKey="day"
            yKey="queries"
            accentColor={chartPalettes.lines.queries}
          />
        </div>
        <div className="engagement-pulse-column">
          <EngagementDistributionCard
            distribution={dashboardData.engagementDistribution}
          />
          <TopStudentsCard students={dashboardData.topActiveStudents} />
        </div>
      </div>
      <div className="engagement-pulse-trends">
        <RecentQuestionsCard
          activity={dashboardData.recentActivity}
          onInspectSession={inspectStudentSession}
        />
      </div>
    </InsightSection>
  );

  const renderConceptIntelligenceSection = () => {
    const conceptHeatmap = dashboardData.conceptDifficulty || [];
    const maxSessions =
      conceptHeatmap.length > 0
        ? Math.max(
            ...conceptHeatmap.map((concept) => concept.sessionCount || 0),
            1
          )
        : 1;
    const normalizedFilter = selectedKeywordFilter.toLowerCase();
    const modalDetails = selectedConceptDetail?.details || [];
    const filteredDetails = normalizedFilter
      ? modalDetails.filter((detail) =>
          (detail.keywords || []).some(
            (keyword) => keyword?.toLowerCase() === normalizedFilter
          )
        )
      : modalDetails;
    const buildModeBreakdown = (details, field, formatter) => {
      if (!details.length) return [];
      const tally = {};
      details.forEach((detail) => {
        const raw = detail[field];
        if (!raw) return;
        const label = formatter(raw);
        if (!label || label === "Not specified") return;
        tally[label] = (tally[label] || 0) + 1;
      });
      return Object.entries(tally)
        .sort((a, b) => b[1] - a[1])
        .map(([label, count]) => ({ label, count }));
    };
    const solutionModeMix = buildModeBreakdown(
      modalDetails,
      "solutionLevel",
      formatSolutionLevelLabel
    );
    const solutionTypeMix = buildModeBreakdown(
      modalDetails,
      "solutionType",
      formatSolutionTypeLabel
    );

    return (
      <InsightSection
        id="concept-intelligence"
        eyebrow="Story 2 · Challenging Concepts"
        title="Concept Sessions"
        summary="Count how many chats explicitly say they're stuck on each concept. Darker cards mean more stuck sessions—tap to see the student names."
      >
        <div className="genai-banner">
          <div className="genai-banner-text">
            <span className="genai-badge">GenAI</span>
            <div>
              <h4>GenAI Concept Insights</h4>
              <p>
                These concept signals use GenAI to detect struggles and extract
                topic keywords. Load them only when needed.
              </p>
            </div>
          </div>
          <div className="genai-banner-actions">
            <button
              type="button"
              className="genai-action-button"
              onClick={fetchGenaiInsights}
              disabled={genaiLoading || !genaiAvailable}
            >
              {genaiLoading
                ? "Loading GenAI..."
                : genaiEnabled
                ? "Refresh GenAI Insights"
                : "Load GenAI Insights"}
            </button>
            {!genaiAvailable && (
              <span className="genai-hint">GenAI not configured</span>
            )}
            {genaiError && <span className="genai-hint error">{genaiError}</span>}
          </div>
        </div>
        <div className="concept-intelligence-grid">
          <div className="concept-intel-panels">
            <div className="concept-summary-panel">
              <div className="concept-stat-grid">
                <div className="concept-stat">
                  <span>Concept Sessions</span>
                  <strong>{conceptSessionTotal}</strong>
                </div>
                <div className="concept-stat">
                  <span>Unique Students</span>
                  <strong>{conceptStudentCount}</strong>
                </div>
              </div>
              {conceptModeSnapshot.levels.length ||
              conceptModeSnapshot.types.length ? (
                <div className="concept-mode-summary global">
                  <div className="concept-mode-card">
                    <div className="concept-keywords-head">
                      <h4>Solution Modes</h4>
                      <span>Strictness requested overall</span>
                    </div>
                    <div className="concept-mode-chip-row">
                      {conceptModeSnapshot.levels.length ? (
                        conceptModeSnapshot.levels.map((item) => (
                          <span
                            className="concept-mode-chip primary"
                            key={`summary-mode-${item.label}`}
                          >
                            {item.label}
                            <small>{item.count}</small>
                          </span>
                        ))
                      ) : (
                        <span className="concept-mode-chip muted">
                          Not logged
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="concept-mode-card">
                    <div className="concept-keywords-head">
                      <h4>Response Style</h4>
                      <span>Concise vs. step-by-step</span>
                    </div>
                    <div className="concept-mode-chip-row">
                      {conceptModeSnapshot.types.length ? (
                        conceptModeSnapshot.types.map((item) => (
                          <span
                            className="concept-mode-chip secondary"
                            key={`summary-type-${item.label}`}
                          >
                            {item.label}
                            <small>{item.count}</small>
                          </span>
                        ))
                      ) : (
                        <span className="concept-mode-chip muted">
                          Not logged
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="concept-keywords">
                <div className="concept-keywords-head">
                  <h4>Top Keywords</h4>
                  <span>
                    {topConceptKeywords.length
                      ? `${topConceptKeywords.length} surfaced`
                      : "No signals yet"}
                  </span>
                </div>
                {topConceptKeywords.length ? (
                  <div className="keyword-chip-row">
                    {topConceptKeywords.map((chip) => (
                      <span className="keyword-chip" key={chip.keyword}>
                        {chip.keyword}
                        <small>{chip.count}</small>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="dashboard-modal-empty compact">
                    We haven’t captured keywords yet.
                  </p>
                )}
              </div>
              {recurringStudents.length ? (
                <div className="concept-overlap">
                  <div className="concept-keywords-head">
                    <h4>Recurring Students</h4>
                    <span>Across concepts</span>
                  </div>
                  <div className="keyword-chip-row">
                    {recurringStudents.map((entry) => (
                      <span className="keyword-chip primary" key={entry.student}>
                        {entry.student}
                        <small>{entry.count}</small>
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
            <DonutChart
              title="Common Error Types"
              data={dashboardData.commonErrors}
              labelKey="type"
              valueKey="percentage"
              palette={chartPalettes.donut}
            />
          </div>
          <div className="concept-heatmap-panel">
            {conceptHeatmap.length === 0 ? (
              <p className="dashboard-modal-empty">
                No students have reported being stuck on a concept yet.
              </p>
            ) : (
              <div className="concept-heatmap-grid">
                {conceptHeatmap.map((concept, index) => {
                  const badge = getRecencyBadge(concept);
                  return (
                  <button
                    key={`concept-heat-${index}`}
                    type="button"
                    className="concept-heatmap-item"
                    onClick={() => openConceptDetail(concept)}
                    style={{
                      backgroundColor: getHeatmapColor(
                        concept.sessionCount ?? 0,
                        Math.max(maxSessions, 1)
                      ),
                    }}
                  >
                    <div className="concept-heatmap-name-row">
                      <span className="concept-heatmap-name">
                        {concept.concept || "Concept"}
                      </span>
                      {badge && (
                        <span
                          className={`concept-recency-badge ${
                            badge.tone
                          }`}
                        >
                          {badge.label}
                        </span>
                      )}
                    </div>
                    <div className="concept-heatmap-value">
                      <strong>{concept.sessionCount ?? 0}</strong>
                      <span>Sessions</span>
                    </div>
                    {renderSparkline(concept.trend)}
                  </button>
                );})}
              </div>
            )}
          </div>
        </div>
        {selectedConceptDetail && (
          <div
            className="dashboard-modal-overlay"
            onClick={closeConceptDetail}
          >
            <div
              className="dashboard-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="dashboard-modal-header">
                <div>
                  <p className="modal-eyebrow">Stuck Sessions</p>
                  <h3>{selectedConceptDetail.concept}</h3>
                  <p className="modal-subtitle">
                    {selectedConceptDetail.sessionCount || 0} chats flagged this
                    concept.
                  </p>
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="modal-secondary-button"
                    onClick={handleCopyConceptSessions}
                  >
                    Copy Student List
                  </button>
                  {copyNotice && (
                    <span className="modal-hint">{copyNotice}</span>
                  )}
                </div>
                <button
                  type="button"
                  className="modal-close-button"
                  onClick={closeConceptDetail}
                >
                  Close
                </button>
              </div>
              <div className="dashboard-modal-body">
                {modalDetails.length ? (
                  <div className="concept-mode-summary">
                    <div className="concept-mode-card">
                      <div className="concept-keywords-head">
                        <h4>Solution Modes</h4>
                        <span>AI response strictness</span>
                      </div>
                      <div className="concept-mode-chip-row">
                        {solutionModeMix.length ? (
                          solutionModeMix.map((item) => (
                            <span
                              className="concept-mode-chip primary"
                              key={`mode-chip-${item.label}`}
                            >
                              {item.label}
                              <small>{item.count}</small>
                            </span>
                          ))
                        ) : (
                          <span className="concept-mode-chip muted">
                            Not logged
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="concept-mode-card">
                      <div className="concept-keywords-head">
                        <h4>Response Style</h4>
                        <span>Concise vs. step-by-step</span>
                      </div>
                      <div className="concept-mode-chip-row">
                        {solutionTypeMix.length ? (
                          solutionTypeMix.map((item) => (
                            <span
                              className="concept-mode-chip secondary"
                              key={`type-chip-${item.label}`}
                            >
                              {item.label}
                              <small>{item.count}</small>
                            </span>
                          ))
                        ) : (
                          <span className="concept-mode-chip muted">
                            Not logged
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}
                {selectedConceptDetail.keywords?.length ? (
                  <div className="concept-keyword-filter">
                    <div className="concept-keywords-head">
                      <h4>Filter by keyword</h4>
                      {selectedKeywordFilter && (
                        <button
                          type="button"
                          className="keyword-filter-clear"
                          onClick={() => setSelectedKeywordFilter("")}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="keyword-chip-row">
                      {selectedConceptDetail.keywords.map((keyword) => (
                        <button
                          type="button"
                          key={`modal-keyword-${keyword}`}
                          className={`keyword-chip ${
                            selectedKeywordFilter === keyword ? "active" : ""
                          }`}
                          onClick={() => setSelectedKeywordFilter(keyword)}
                        >
                          {keyword}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
                {filteredDetails.length ? (
                  <ul className="concept-session-list">
                    {filteredDetails.map((detail) => (
                      <li key={`${detail.sessionId}-${detail.username}`}>
                        <div className="concept-session-meta">
                          <div>
                            <strong>{detail.student || "Student"}</strong>
                            <span>{detail.relativeTime}</span>
                          </div>
                          <small>{detail.sessionId}</small>
                        </div>
                        <button
                          type="button"
                          className="concept-session-snippet"
                          onClick={() =>
                            openConversationDetail(detail, {
                              sessionOwner: detail.username,
                              sessionId: detail.sessionId,
                              studentName: detail.student,
                            })
                          }
                        >
                          {detail.snippet || "View chat"}
                        </button>
                        {(detail.solutionLevel || detail.solutionType) && (
                          <div className="concept-session-modes">
                            {detail.solutionLevel ? (
                              <span className="concept-mode-chip primary">
                                {formatSolutionLevelLabel(
                                  detail.solutionLevel
                                )}
                              </span>
                            ) : null}
                            {detail.solutionType ? (
                              <span className="concept-mode-chip secondary">
                                {formatSolutionTypeLabel(detail.solutionType)}
                              </span>
                            ) : null}
                          </div>
                        )}
                        {detail.keywords?.length ? (
                          <p className="concept-session-keywords">
                            <strong>Keywords:</strong>{" "}
                            {detail.keywords.join(", ")}
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="dashboard-modal-empty">
                    {selectedKeywordFilter
                      ? "No sessions mention that keyword."
                      : "No additional detail is available for this concept yet."}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </InsightSection>
    );
  };

  const renderLearningFrictionSection = () => (
    <InsightSection
      id="learning-friction"
      eyebrow="Story 3 · Unproductive Patterns"
      title="Learning Friction Signals"
      summary="Combine copy/paste attempts, inactivity, and hint skipping into a single command center so interventions can happen early."
    >
      <div className="learning-friction-grid">
        <div className="friction-status-column">
          <div className="friction-card alert-system-panel">
            <div className="friction-card-head">
              <div>
                <p className="friction-card-eyebrow">Live Alerts</p>
                <h4>{liveAlerts} open signals</h4>
              </div>
              <span className="friction-card-note">
                Auto-detected from active sessions
              </span>
            </div>
            {alertTypeSummary.length ? (
              <div className="alert-system-summary">
                {alertTypeSummary.map((item) => (
                  <div key={item.label} className="alert-system-chip">
                    <span>{item.label}</span>
                    <strong>{item.count}</strong>
                  </div>
                ))}
              </div>
            ) : (
              <p className="dashboard-modal-empty compact">
                No alert categories have triggered yet.
              </p>
            )}
          </div>
          <div className="friction-card">
            <div className="friction-card-head">
              <div>
                <p className="friction-card-eyebrow">Feedback heat</p>
                <h4>Students needing clarity</h4>
              </div>
              <span>{feedbackAlerts.length} noted</span>
            </div>
            {feedbackAlerts.length ? (
              <ul className="friction-mini-list">
                {feedbackAlerts.map((student, index) => (
                  <li key={`feedback-${index}`}>
                    <div>
                      <strong>{student.name}</strong>
                      <p>{student.lastActive}</p>
                    </div>
                    <span>{student.totalQueries} queries</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="dashboard-modal-empty compact">
                No downvote spikes detected.
              </p>
            )}
          </div>
          <div className="friction-card">
            <div className="friction-card-head">
              <div>
                <p className="friction-card-eyebrow">Follow-up load</p>
                <h4>Who keeps returning?</h4>
              </div>
              <span>{followUpHotspots.length} students</span>
            </div>
            {followUpHotspots.length ? (
              <ul className="friction-mini-list">
                {followUpHotspots.map((student, index) => (
                  <li key={`follow-${index}`}>
                    <div>
                      <strong>{student.name}</strong>
                      <p>{student.multiTurnSessions} multi-turn sessions</p>
                    </div>
                    <span>{student.followUps} follow-ups</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="dashboard-modal-empty compact">
                No repetitive sessions logged yet.
              </p>
            )}
          </div>
          <div className="friction-card">
            <div className="friction-card-head">
              <div>
                <p className="friction-card-eyebrow">Cooling off</p>
                <h4>Used the tutor last week</h4>
              </div>
              <span>{coolingOffList.length} students</span>
            </div>
            {coolingOffList.length ? (
              <ul className="friction-mini-list">
                {coolingOffList.map((student, index) => (
                  <li key={`cooling-${index}`}>
                    <div>
                      <strong>{student.name}</strong>
                      <p>{student.lastActive}</p>
                    </div>
                    <span>{student.weeklyQueries} queries</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="dashboard-modal-empty compact">
                No students have gone cold after recent bursts.
              </p>
            )}
          </div>
          <div className="friction-card">
            <div className="friction-card-head">
              <div>
                <p className="friction-card-eyebrow">Concept overload</p>
                <h4>Students juggling topics</h4>
              </div>
              <span>{conceptStackList.length} students</span>
            </div>
            {conceptStackList.length ? (
              <ul className="friction-mini-list">
                {conceptStackList.map((student) => (
                  <li key={`concept-stack-${student.username || student.name}`}>
                    <div>
                      <strong>{student.name}</strong>
                      <div className="profile-concepts-row">
                        {student.concepts.slice(0, 2).map((concept) => (
                          <span
                            key={`${student.username || student.name}-${
                              concept.concept
                            }`}
                            className="profile-concept-chip secondary"
                          >
                            {concept.concept}
                            <small>{concept.count}</small>
                          </span>
                        ))}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="dashboard-modal-empty compact">
                No multi-concept struggles detected.
              </p>
            )}
          </div>
        </div>
        <div className="friction-alert-feed">
          {dashboardData.alerts?.length ? (
            dashboardData.alerts.map((alert, index) => (
              <AlertCard key={`alert-${index}`} alert={alert} />
            ))
          ) : (
            <p className="dashboard-modal-empty">
              No unproductive learning patterns have been flagged.
            </p>
          )}
        </div>
      </div>
    </InsightSection>
  );

  const renderStudentProgressSection = () => (
    <InsightSection
      id="student-progress"
      eyebrow="Story 4 · Individual Progress"
      title="Student Progress Explorer"
      summary="Filter and search across every student to understand how their questioning evolves before the next office-hour conversation."
    >
      <StudentProfiles
        profiles={dashboardData.studentProfiles}
        onInspectSession={inspectStudentSession}
        onDownloadSession={handleDownloadConversation}
      />
    </InsightSection>
  );

  const renderAiAssistanceSection = () => (
    <InsightSection
      id="ai-assistance"
      eyebrow="Story 5 · AI Guardrails"
      title="AI Assistance Health"
      summary="Balance hints and answers over time and check whether the guidance is still accurate, cited, and voted helpful by students."
    >
      <div className="hint-controls-grid">
        <StackedBarChart
          title="Hints vs. Answers"
          data={dashboardData.hintUsage}
          labelKey="week"
        />
        <QualityMetrics data={dashboardData.aiResponseQuality} />
      </div>
    </InsightSection>
  );

  const insightCards = [
    {
      id: "engagement-pulse",
      label: "Engagement Pulse",
      description: "Usage volume, roster momentum, and active trends.",
      metricLabel: "Active today",
      metricValue: overview.activeToday || 0,
      icon: FiActivity,
      accent: "#0ea5e9",
      accentSoft: "#f0f9ff",
      render: renderEngagementPulseSection,
    },
    {
      id: "concept-intelligence",
      label: "Concept Intelligence",
      description: "Find the concepts that trigger the most confusion.",
      metricLabel: "Concept sessions",
      metricValue: conceptSessionTotal,
      icon: FiPieChart,
      badge: "GenAI",
      accent: "#f97316",
      accentSoft: "#fff7ed",
      render: renderConceptIntelligenceSection,
    },
    {
      id: "learning-friction",
      label: "Learning Friction",
      description: "Surface copy/paste, retries, and hint skipping.",
      metricLabel: "Open alerts",
      metricValue: liveAlerts,
      icon: FiAlertTriangle,
      accent: "#f59e0b",
      accentSoft: "#fffbeb",
      render: renderLearningFrictionSection,
    },
    {
      id: "student-progress",
      label: "Student Progress Explorer",
      description: "Search and filter to trace individual progress.",
      metricLabel: "Profiles tracked",
      metricValue: studentProfilesCount,
      icon: FiUserCheck,
      accent: "#3b82f6",
      accentSoft: "#eff6ff",
      render: renderStudentProgressSection,
    },
    {
      id: "ai-assistance",
      label: "AI Assistance Health",
      description: "Monitor hints vs. solutions and quality signals.",
      metricLabel: "Hints this week",
      metricValue: hintsThisWeek,
      icon: FiCpu,
      accent: "#a855f7",
      accentSoft: "#faf5ff",
      render: renderAiAssistanceSection,
    },
  ];

  const selectedInsight = activeInsight
    ? insightCards.find((item) => item.id === activeInsight)
    : null;

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <h2>Error Loading Dashboard</h2>
        <p>{error}</p>
        <button onClick={fetchDashboardData} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  const headerSubtitle = courseName
    ? `Monitoring ${courseName} in real time.`
    : "Monitor every cohort without leaving this screen.";

  const quickStats = [
    { label: "Active Students", value: overview.activeToday || 0 },
    { label: "Concept Sessions", value: conceptSessionTotal },
    { label: "Open Alerts", value: liveAlerts },
    { label: "Hints This Week", value: hintsThisWeek },
  ];

  return (
    <>
      <div className="teachers-dashboard">
        <header className="dashboard-header">
        <div className="header-bar minimal">
          <div className="header-left">
            <button
              className="back-button"
              onClick={handleBack}
              aria-label="Back to courses"
            >
              <FiArrowLeft />
              <span>Back to Courses</span>
            </button>
          </div>
          <div className="header-center">
            <div className="header-identity">
              <p className="header-eyebrow">Teacher workspace</p>
              <div className="header-title-row">
                <h1 className="header-title">Instructor Control</h1>
                <span className="header-status-pill">Live</span>
              </div>
              <p className="header-subtitle">{headerSubtitle}</p>
            </div>
            <div className="course-chip">
              <span className="chip-label">{courseNumber || "Course"}</span>
              <span className="chip-name">{courseName || "Workspace"}</span>
            </div>
          </div>
          <div className="header-right">
            {canManageStudyFeatures && (
              <div className="genai-banner-actions">
                <button
                  type="button"
                  className="genai-action-button"
                  onClick={toggleStudyFeatures}
                  disabled={studyFeaturesLoading}
                  title={
                    studyFeaturesEnabled
                      ? "Study Coach / Practice Questions are enabled"
                      : "Study Coach / Practice Questions are disabled"
                  }
                >
                  {studyFeaturesLoading
                    ? "Updating…"
                    : studyFeaturesEnabled
                      ? "Disable Study Features"
                      : "Enable Study Features"}
                </button>
                {studyFeaturesError && (
                  <span className="genai-hint error">{studyFeaturesError}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

        {selectedInsight ? (
        <section className="insight-detail-view" aria-live="polite">
          <div className="detail-header">
            <button
              type="button"
              className="back-to-overview"
              onClick={() => setActiveInsight(null)}
            >
              <FiArrowLeft />
              <span>Back to Overview</span>
            </button>
            <div className="detail-meta">
              <p className="section-eyebrow">Deep Dive</p>
              <h2>{selectedInsight.label}</h2>
              <p className="section-summary">{selectedInsight.description}</p>
            </div>
            <div className="detail-metric">
              <span>{selectedInsight.metricLabel}</span>
              <strong>{selectedInsight.metricValue ?? 0}</strong>
            </div>
          </div>
          <div className="detail-body">{selectedInsight.render()}</div>
        </section>
        ) : (
        <section
          className="insight-overview"
          aria-labelledby="insight-overview-title"
        >
          <div className="insight-overview-header">
            <div>
              <p className="section-eyebrow">Story-driven tiles</p>
              <h2 id="insight-overview-title">
                Five views answer the instructor stories
              </h2>
              <p className="section-summary">
                Engagement, concepts, friction, individual progress, and AI
                assistance each get a merged tile. Dive in only when you need
                the expanded screen.
              </p>
            </div>
            <div className="overview-summary">
              {quickStats.map((stat) => (
                <div key={stat.label} className="overview-chip">
                  <span>{stat.label}</span>
                  <strong>{stat.value}</strong>
                </div>
              ))}
            </div>
          </div>
          <div className="insight-card-grid">
            {insightCards.map((card) => {
              const Icon = card.icon;
              return (
                <button
                  key={card.id}
                  type="button"
                  className="insight-card"
                  onClick={() => setActiveInsight(card.id)}
                  aria-label={`View ${card.label}`}
                  style={{
                    "--card-accent": card.accent,
                    "--card-accent-soft": card.accentSoft,
                  }}
                >
                  <div className="insight-card-head">
                    <div className="insight-card-icon" aria-hidden="true">
                      <Icon />
                    </div>
                    <div className="insight-card-heading">
                      <div className="insight-card-eyebrow">
                        {card.metricLabel}
                      </div>
                      <h3>{card.label}</h3>
                    </div>
                    {card.badge && (
                      <span className="insight-card-badge">
                        {card.badge}
                      </span>
                    )}
                  </div>
                  <p>{card.description}</p>
                  <div className="insight-card-metric">
                    <strong>{card.metricValue ?? 0}</strong>
                    <span>{card.metricLabel}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
        )}
      </div>
      {selectedConversation && (
        <div
          className="dashboard-modal-overlay"
          onClick={closeConversationDetail}
        >
          <div
            className="dashboard-modal conversation-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="dashboard-modal-header">
              <div>
                <p className="modal-eyebrow">Session transcript</p>
                <h3>{selectedConversation.student || "Student"}</h3>
                <p className="modal-subtitle">
                  Session {selectedConversation.sessionId}
                </p>
              </div>
              <button
                type="button"
                className="modal-close-button"
                onClick={closeConversationDetail}
              >
                Close
              </button>
            </div>
            <div className="dashboard-modal-body conversation-body">
              <div className="conversation-log">
                {(selectedConversation.conversation || []).map(
                  (message, index) => {
                    const isStudent = message.role === "Student";
                    const highlight =
                      isStudent &&
                      selectedConversation.snippet &&
                      message.text
                        ?.toLowerCase()
                        ?.includes(selectedConversation.snippet.toLowerCase());
                    return (
                      <div
                        key={`conversation-${index}`}
                        className={`conversation-line ${
                          isStudent ? "student" : "tutor"
                        } ${highlight ? "highlight" : ""}`}
                      >
                        <strong>{message.role}:</strong>
                        {isStudent ? (
                          <p>
                            {highlight
                              ? renderHighlightedText(
                                  message.text,
                                  selectedConversation.snippet
                                )
                              : message.text}
                          </p>
                        ) : (
                          <div className="conversation-markdown">
                            <MarkdownRenderer>{message.text}</MarkdownRenderer>
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
              <div className="conversation-insights">
                <div className="conversation-insight-card">
                  <div className="insight-card-head">
                    <div>
                      <p className="insight-eyebrow">Storyline</p>
                      <h4>Session Summary</h4>
                    </div>
                    <span className="genai-badge">GenAI</span>
                    <button
                      type="button"
                      className="insight-action-button"
                      onClick={handleGenerateConversationSummary}
                      disabled={
                        conversationSummary.loading || !activeConversationMeta
                      }
                    >
                      {conversationSummary.loading
                        ? "Generating..."
                        : "Generate"}
                    </button>
                  </div>
                  <p
                    className={`conversation-insight-body${
                      conversationSummary.error ? " error" : ""
                    }`}
                  >
                    {conversationSummary.loading
                      ? "Asking the tutor to summarize..."
                      : conversationSummary.error ||
                        conversationSummary.text ||
                        "Generate a quick narrative for this chat."}
                  </p>
                </div>
                <div className="conversation-insight-card">
                  <div className="insight-card-head">
                    <div>
                      <p className="insight-eyebrow">Intervention</p>
                      <h4>Areas of Challenge</h4>
                    </div>
                    <span className="genai-badge">GenAI</span>
                    <button
                      type="button"
                      className="insight-action-button"
                      onClick={handleAnalyzeConversationChallenges}
                      disabled={
                        conversationChallenges.loading ||
                        !activeConversationMeta
                      }
                    >
                      {conversationChallenges.loading
                        ? "Analyzing..."
                        : "Analyze"}
                    </button>
                  </div>
                  <p
                    className={`conversation-insight-body${
                      conversationChallenges.error ? " error" : ""
                    }`}
                  >
                    {conversationChallenges.loading
                      ? "Inspecting the chat for friction..."
                      : conversationChallenges.error ||
                        conversationChallenges.text ||
                        "Surface misconceptions or stuck points on demand."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TeachersDashboard;
